import type { ComponentProps } from "react"

import { type ContactRole, RoleGlyph } from "@/components/ui/role-glyph"
import { Avatar as KitAvatar } from "@/components/ui-kit/untitled/base/avatar/avatar"

import { cx } from "@uui/utils/cx"

/*
 * Staff appear with a photograph or their initials. The
 * identity-free claim protects students; staff are not students. Initials sit
 * in stone.800 on stone.100 inside a stone.300 ring, 24 to 40px. No presence
 * dot (nothing in the product knows who is online) and no verified tick, so
 * `status` and `verified` are not props here. `tone="print"` is the rail's
 * foot: stone.50 initials on the on-print ring.
 *
 * A school contact has no photograph, and initials beside the name say the
 * name twice, so their disc carries the role glyph instead:
 * `role` draws it in the same ink as the initials, the articulation in do.500,
 * at 16, 18 and 22 for the three sizes. A person with several roles is given
 * the most senior.
 */
export type AvatarSize = "xs" | "sm" | "md"
export type AvatarTone = "paper" | "print"

export type AvatarProps = Omit<
  ComponentProps<typeof KitAvatar>,
  "status" | "verified" | "badge" | "contrastBorder" | "size"
> & {
  /** 24 / 32 / 40 */
  size?: AvatarSize
  tone?: AvatarTone
  /** a school contact's role, drawn in place of initials */
  role?: ContactRole
}

const TONE: Record<AvatarTone, string> = {
  paper: "bg-stone-100 ring-stone-300 [&>span]:text-stone-800",
  print: "ring-onprint-edge bg-stone-50/14 [&>span]:text-stone-50",
}

const INITIALS: Record<AvatarSize, string> = {
  xs: "[&>span]:text-[9.5px]",
  sm: "[&>span]:text-[11px]",
  md: "[&>span]:text-[13px]",
}

const GLYPH: Record<AvatarSize, string> = {
  xs: "size-4",
  sm: "size-[18px]",
  md: "size-[22px]",
}

const GLYPH_INK: Record<AvatarTone, string> = {
  paper: "text-stone-800",
  print: "text-stone-50",
}

export function Avatar({
  size = "sm",
  tone = "paper",
  role,
  initials,
  className,
  ...props
}: AvatarProps) {
  return (
    <KitAvatar
      size={size}
      contrastBorder={false}
      initials={role ? undefined : initials}
      placeholder={
        role ? (
          <RoleGlyph role={role} className={cx(GLYPH[size], GLYPH_INK[tone])} />
        ) : undefined
      }
      className={cx(
        "ring-1 ring-inset [&>span]:font-semibold",
        TONE[tone],
        INITIALS[size],
        className,
      )}
      {...props}
    />
  )
}
